import { useState, useRef, useCallback, useEffect } from 'react'
import { api } from '@/services/api-client'
import type { UserFile } from '@/types'
import {
  Upload, Trash2, Download, Edit2, X, Check, Search, File,
  FileText, FileImage, FileCode, FileJson, Music, Video,
  FolderOpen, RefreshCw, ChevronDown, ChevronUp, MoreVertical,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'

const TEXT_MIMES = ['text/', 'application/json', 'application/xml', 'application/javascript',
  'application/typescript', 'application/x-yaml', 'application/x-sh', 'application/sql',
  'application/graphql', 'application/ld+json', 'application/manifest+json']

function isText(mime: string) { return TEXT_MIMES.some(p => mime.startsWith(p)) }

function fileIcon(mime: string) {
  if (mime.startsWith('image/')) return <FileImage size={18} className="fm-file-icon fm-icon-image" />
  if (mime.startsWith('video/')) return <Video size={18} className="fm-file-icon fm-icon-video" />
  if (mime.startsWith('audio/')) return <Music size={18} className="fm-file-icon fm-icon-audio" />
  if (mime.includes('json')) return <FileJson size={18} className="fm-file-icon fm-icon-json" />
  if (mime.startsWith('text/') || mime.includes('javascript') || mime.includes('typescript') || mime.includes('xml') || mime.includes('yaml') || mime.includes('sh') || mime.includes('sql'))
    return <FileCode size={18} className="fm-file-icon fm-icon-code" />
  if (mime === 'application/pdf') return <FileText size={18} className="fm-file-icon fm-icon-pdf" />
  return <File size={18} className="fm-file-icon fm-icon-other" />
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

type SortKey = 'name' | 'size' | 'updated_at' | 'mime_type'

export function FileManagerView() {
  const [files, setFiles] = useState<UserFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('updated_at')
  const [sortAsc, setSortAsc] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [editingFile, setEditingFile] = useState<UserFile | null>(null)
  const [editContent, setEditContent] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameVal, setRenameVal] = useState('')
  const [error, setError] = useState('')
  const dragCounter = useRef(0)

  const loadFiles = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.getFiles()
      setFiles(data)
    } catch (e: any) { setError(e.message || 'Błąd ładowania') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadFiles() }, [loadFiles])

  // ── Upload ──────────────────────────────────────────────────────────────────
  const uploadFiles = async (fileList: FileList | File[]) => {
    const items = Array.from(fileList)
    if (!items.length) return
    setUploading(true)
    setError('')
    try {
      const fd = new FormData()
      items.forEach(f => fd.append('files', f))
      const saved = await api.uploadFiles(fd)
      setFiles(prev => [...saved, ...prev])
    } catch (e: any) { setError(e.message || 'Błąd przesyłania') }
    finally { setUploading(false) }
  }

  // ── Drag & Drop ─────────────────────────────────────────────────────────────
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) setDragging(true)
  }
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current--
    if (dragCounter.current === 0) setDragging(false)
  }
  const onDragOver = (e: React.DragEvent) => { e.preventDefault() }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = 0
    setDragging(false)
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files)
  }

  // ── Selection ───────────────────────────────────────────────────────────────
  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(f => f.id)))
  }

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteSelected = async () => {
    if (!selected.size) return
    if (!confirm(`Usunąć ${selected.size} ${selected.size === 1 ? 'plik' : 'pliki/plików'}?`)) return
    const ids = [...selected]
    try {
      await api.bulkDeleteFiles(ids)
      setFiles(prev => prev.filter(f => !ids.includes(f.id)))
      setSelected(new Set())
    } catch (e: any) { setError(e.message || 'Błąd usuwania') }
  }

  const deleteSingle = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('Usunąć plik?')) return
    try {
      await api.deleteFile(id)
      setFiles(prev => prev.filter(f => f.id !== id))
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n })
    } catch (e: any) { setError(e.message || 'Błąd usuwania') }
  }

  // ── Download ─────────────────────────────────────────────────────────────────
  const download = async (file: UserFile, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const data = await api.getFileContent(file.id)
      let blob: Blob
      if (isText(file.mime_type)) {
        blob = new Blob([data.content!], { type: file.mime_type || 'text/plain' })
      } else {
        const binary = atob(data.content!)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        blob = new Blob([bytes], { type: file.mime_type || 'application/octet-stream' })
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = file.name; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e: any) { setError(e.message || 'Błąd pobierania') }
  }

  // ── Edit text ────────────────────────────────────────────────────────────────
  const openEdit = async (file: UserFile, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isText(file.mime_type)) return
    try {
      const data = await api.getFileContent(file.id)
      setEditingFile(file)
      setEditContent(data.content || '')
    } catch (e: any) { setError(e.message || 'Błąd otwierania') }
  }

  const saveEdit = async () => {
    if (!editingFile) return
    setEditSaving(true)
    try {
      const updated = await api.updateFile(editingFile.id, { content: editContent })
      setFiles(prev => prev.map(f => f.id === updated.id ? { ...f, ...updated } : f))
      setEditingFile(null)
    } catch (e: any) { setError(e.message || 'Błąd zapisu') }
    finally { setEditSaving(false) }
  }

  // ── Rename ──────────────────────────────────────────────────────────────────
  const startRename = (file: UserFile, e: React.MouseEvent) => {
    e.stopPropagation()
    setRenamingId(file.id)
    setRenameVal(file.name)
  }
  const commitRename = async (id: string) => {
    if (!renameVal.trim()) { setRenamingId(null); return }
    try {
      const updated = await api.updateFile(id, { name: renameVal.trim() })
      setFiles(prev => prev.map(f => f.id === id ? { ...f, ...updated } : f))
    } catch {}
    setRenamingId(null)
  }

  // ── Sort & Filter ────────────────────────────────────────────────────────────
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(a => !a)
    else { setSortKey(key); setSortAsc(true) }
  }

  const filtered = files
    .filter(f => f.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let cmp = 0
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name)
      else if (sortKey === 'size') cmp = a.size - b.size
      else if (sortKey === 'updated_at') cmp = a.updated_at.localeCompare(b.updated_at)
      else if (sortKey === 'mime_type') cmp = a.mime_type.localeCompare(b.mime_type)
      return sortAsc ? cmp : -cmp
    })

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k ? (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null

  return (
    <div
      className={`file-manager${dragging ? ' fm-dragging' : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Drag overlay */}
      {dragging && (
        <div className="fm-drop-overlay">
          <div className="fm-drop-overlay-inner">
            <Upload size={48} />
            <p>Upuść pliki tutaj</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="fm-header">
        <div className="fm-header-left">
          <FolderOpen size={20} />
          <h1>Pliki</h1>
          <span className="fm-count">{files.length} plików</span>
        </div>
        <div className="fm-header-actions">
          <div className="fm-search">
            <Search size={14} />
            <input
              placeholder="Szukaj..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="fm-search-input"
            />
            {search && <button onClick={() => setSearch('')}><X size={12} /></button>}
          </div>
          <button className="button-secondary fm-btn" onClick={loadFiles} title="Odśwież">
            <RefreshCw size={14} />
          </button>
          {selected.size > 0 && (
            <button className="button-danger fm-btn" onClick={deleteSelected}>
              <Trash2 size={14} /> Usuń ({selected.size})
            </button>
          )}
          <label
            className={`button-primary fm-btn${uploading ? ' fm-btn-disabled' : ''}`}
            style={{ cursor: uploading ? 'not-allowed' : 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <input
              type="file"
              multiple
              disabled={uploading}
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = '' }}
            />
            <Upload size={14} /> {uploading ? 'Przesyłanie…' : 'Dodaj pliki'}
          </label>
        </div>
      </div>

      {error && (
        <div className="fm-error">
          {error}
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      {/* Table */}
      <div className="fm-table-wrap">
        <table className="fm-table">
          <thead>
            <tr>
              <th className="fm-th-check">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={selectAll}
                  className="fm-checkbox"
                />
              </th>
              <th className="fm-th-icon" />
              <th className="fm-th-name fm-sortable" onClick={() => toggleSort('name')}>
                Nazwa <SortIcon k="name" />
              </th>
              <th className="fm-th-type fm-sortable" onClick={() => toggleSort('mime_type')}>
                Typ <SortIcon k="mime_type" />
              </th>
              <th className="fm-th-size fm-sortable" onClick={() => toggleSort('size')}>
                Rozmiar <SortIcon k="size" />
              </th>
              <th className="fm-th-date fm-sortable" onClick={() => toggleSort('updated_at')}>
                Modyfikacja <SortIcon k="updated_at" />
              </th>
              <th className="fm-th-actions" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="fm-loading">Ładowanie…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7}>
                  {search ? (
                    <div className="fm-empty"><p>Brak wyników</p></div>
                  ) : (
                    <label className="fm-empty fm-empty-drop" style={{ cursor: 'pointer' }}>
                      <input type="file" multiple style={{ display: 'none' }}
                        onChange={e => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = '' }} />
                      <Upload size={40} />
                      <p>Przeciągnij pliki tutaj lub kliknij, aby dodać</p>
                    </label>
                  )}
                </td>
              </tr>
            )}
            {filtered.map(file => (
              <tr
                key={file.id}
                className={`fm-row${selected.has(file.id) ? ' selected' : ''}`}
                onDoubleClick={(e) => isText(file.mime_type) ? openEdit(file, e) : download(file, e as any)}
              >
                <td className="fm-td-check" onClick={e => toggleSelect(file.id, e)}>
                  <input
                    type="checkbox"
                    checked={selected.has(file.id)}
                    onChange={() => {}}
                    className="fm-checkbox"
                    onClick={e => e.stopPropagation()}
                  />
                </td>
                <td className="fm-td-icon">{fileIcon(file.mime_type)}</td>
                <td className="fm-td-name">
                  {renamingId === file.id ? (
                    <input
                      className="fm-rename-input"
                      value={renameVal}
                      autoFocus
                      onChange={e => setRenameVal(e.target.value)}
                      onBlur={() => commitRename(file.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename(file.id)
                        if (e.key === 'Escape') setRenamingId(null)
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <span className="fm-filename" title={file.name}>{file.name}</span>
                  )}
                </td>
                <td className="fm-td-type">
                  <span className="fm-mime">{file.mime_type || '—'}</span>
                </td>
                <td className="fm-td-size">{formatSize(file.size)}</td>
                <td className="fm-td-date">{formatDate(file.updated_at)}</td>
                <td className="fm-td-actions">
                  <div className="fm-row-actions">
                    {isText(file.mime_type) && (
                      <button className="fm-action-btn" onClick={e => openEdit(file, e)} title="Edytuj">
                        <Edit2 size={13} />
                      </button>
                    )}
                    <button className="fm-action-btn" onClick={e => startRename(file, e)} title="Zmień nazwę">
                      <MoreVertical size={13} />
                    </button>
                    <button className="fm-action-btn" onClick={e => download(file, e)} title="Pobierz">
                      <Download size={13} />
                    </button>
                    <button className="fm-action-btn danger" onClick={e => deleteSingle(file.id, e)} title="Usuń">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Text editor modal */}
      {editingFile && (
        <div className="fm-modal-overlay" onClick={() => setEditingFile(null)}>
          <div className="fm-modal" onClick={e => e.stopPropagation()}>
            <div className="fm-modal-header">
              <div className="fm-modal-title">
                {fileIcon(editingFile.mime_type)}
                <span>{editingFile.name}</span>
              </div>
              <div className="fm-modal-header-actions">
                <button
                  className="button-primary fm-btn"
                  onClick={saveEdit}
                  disabled={editSaving}
                >
                  <Check size={14} /> {editSaving ? 'Zapisywanie…' : 'Zapisz'}
                </button>
                <button className="fm-modal-close" onClick={() => setEditingFile(null)}>
                  <X size={16} />
                </button>
              </div>
            </div>
            <textarea
              className="fm-editor"
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              spellCheck={false}
            />
          </div>
        </div>
      )}
    </div>
  )
}
